/**
 * Action Tag Configuration System
 * 
 * Centralized, declarative configuration for action tags like [Cost 2 Resilience], [Gain 5 HP], etc.
 * This provides a single source of truth for patterns, verbs, resources, and behavior.
 */

import { TagActionType } from '~/pages/CharacterSheet/types';
import { TagRenderContext } from '~/utils/tagRenderer';

/**
 * Shared verb lists for positive and negative actions
 */
const POSITIVE_VERBS = ['Gain', 'Restore', 'Restor', 'Acquire', 'Accumulate'];
const NEGATIVE_VERBS = ['Lose', 'Spend', 'Pay', 'Cost', 'Suffer', 'Take'];

/**
 * Resource definitions with their display names/aliases and state keys
 */
export const RESOURCES = {
  health: {
    names: ['Health', 'HP'],
    stateKey: 'currentHp' as const,
  },
  resilience: {
    names: ['Resilience', 'RES'],
    stateKey: 'currentResilience' as const,
  },
  strain: {
    names: ['Strain'],
    stateKey: null, // Strain doesn't have a "current" value to check
  },
} as const;

type ResourceKey = keyof typeof RESOURCES;

/**
 * Action tag configuration for a single action type
 */
interface ActionTagConfigEntry {
  resource: ResourceKey;
  direction: 'gain' | 'lose';
  verbs: readonly string[];
  style: 'positive' | 'negative';
  canAfford: (state: TagRenderContext, value: number) => boolean;
  pattern?: RegExp; // Generated at module load time
}

/**
 * Core configuration mapping TagActionType to its behavior
 */
const ACTION_TAG_CONFIG: Record<TagActionType, ActionTagConfigEntry> = {
  [TagActionType.GAIN_HEALTH]: {
    resource: 'health',
    direction: 'gain',
    verbs: POSITIVE_VERBS,
    style: 'positive',
    canAfford: () => true, // Can always gain health
  },
  [TagActionType.LOSE_HEALTH]: {
    resource: 'health',
    direction: 'lose',
    verbs: NEGATIVE_VERBS,
    style: 'negative',
    canAfford: (state, value) => state.currentHp >= value,
  },
  [TagActionType.LOSE_RESILIENCE]: {
    resource: 'resilience',
    direction: 'lose',
    verbs: NEGATIVE_VERBS,
    style: 'negative',
    canAfford: (state, value) => state.currentResilience >= value,
  },
  [TagActionType.ACQUIRE_STRAIN]: {
    resource: 'strain',
    direction: 'gain',
    verbs: POSITIVE_VERBS,
    style: 'negative',
    canAfford: () => true, // Can always acquire strain
  },
  [TagActionType.REDUCE_STRAIN]: {
    resource: 'strain',
    direction: 'lose',
    verbs: NEGATIVE_VERBS,
    style: 'positive',
    canAfford: () => true, // Can always acquire strain
  },
};

/**
 * Builds a regex pattern from an action tag configuration entry
 */
function buildPattern(config: ActionTagConfigEntry): RegExp {
  const resource = RESOURCES[config.resource];

  // Build verb pattern with conjugations, handling English grammar rules:
  // - Verbs ending in 'e': drop 'e' before adding 'ing' (Restore -> Restoring)
  // - All verbs: can add 's', 'ing', 'ed'
  const verbPattern = config.verbs.map(v => {
    if (v.endsWith('e')) {
      // For verbs ending in 'e': support base, +s, +d, or drop 'e' and add 'ing'
      const stem = v.slice(0, -1); // Remove the 'e'
      return `${v}(s|d)?|${stem}ing`;
    } else {
      // For other verbs: support base, +s, +ing, +ed
      return `${v}(s|ing|ed)?`;
    }
  }).join('|');

  // Build resource name pattern from aliases
  const resourcePattern = resource.names.join('|');

  // Determine sign character based on direction
  const sign = config.direction === 'gain' ? '\\+?' : '-?';

  // Pattern structure: [optional prefix with sign] [verb with optional colon and sign] [number] [resource]
  // Examples:
  //   "Gain 5 Health"
  //   "+5 HP"
  //   "Cost 2 Resilience"
  //   "Costs 3 RES"
  //   "Costing 4 Resilience"
  //   "Restoring 2 Resilience" (handles dropped 'e')
  //   "Acquiring 3 HP" (handles dropped 'e')
  //   "Action: -2 HP"
  return new RegExp(
    `^((\\w+:? )?${sign}(${verbPattern}):? ${sign}|${sign})?(\\d+) (${resourcePattern})$`,
    'i'
  );
}

// Generate patterns at module load time
for (const config of Object.values(ACTION_TAG_CONFIG)) {
  config.pattern = buildPattern(config);
}

/**
 * Result of matching an action tag
 */
export interface ActionTagMatch {
  actionType: TagActionType;
  value: number;
  style: 'positive' | 'negative';
  canAfford: boolean;
  resourceName: string;
}

/**
 * Extracts the numeric value from action tag text
 */
function extractValue(text: string): number {
  const match = text.match(/(\d+)/i);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Matches text against action tag patterns and returns match info
 * 
 * @param text - The bracketed text (without brackets)
 * @param state - Character state for affordability checks
 * @returns Match info if text matches an action tag pattern, null otherwise
 */
export function matchActionTag(
  text: string,
  state: TagRenderContext
): ActionTagMatch | null {
  for (const [actionType, config] of Object.entries(ACTION_TAG_CONFIG) as Array<[TagActionType, ActionTagConfigEntry]>) {
    if (config.pattern?.test(text)) {
      const value = extractValue(text);
      const resource = RESOURCES[config.resource];

      return {
        actionType,
        value,
        style: config.style,
        canAfford: config.canAfford(state, value),
        resourceName: resource.names[0], // Use primary resource name
      };
    }
  }

  return null;
}

/**
 * Checks if text matches any action tag pattern
 * 
 * @param text - The bracketed text (without brackets)
 * @returns true if text matches an action tag pattern
 */
export function isActionTag(text: string): boolean {
  for (const config of Object.values(ACTION_TAG_CONFIG)) {
    if (config.pattern?.test(text)) {
      return true;
    }
  }
  return false;
}

/**
 * Gets the generated patterns for backward compatibility
 */
export function getGeneratedPatterns() {
  return {
    gainHealth: ACTION_TAG_CONFIG[TagActionType.GAIN_HEALTH].pattern!,
    loseHealth: ACTION_TAG_CONFIG[TagActionType.LOSE_HEALTH].pattern!,
    loseResilience: ACTION_TAG_CONFIG[TagActionType.LOSE_RESILIENCE].pattern!,
    acquireStrain: ACTION_TAG_CONFIG[TagActionType.ACQUIRE_STRAIN].pattern!,
  };
}
